import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { toast } from "react-hot-toast";
import { formatDateToDDMMYYYY } from "./const";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import { showRefinery } from "../erpClient";
import {
  isRefineryApiSuccess,
  mapRefineryShowToForm,
} from "../utils/refineryErpAdapter";

const COLUMNS = [
  { id: "no", label: "No.", width: "40px" },
  { id: "productGroup", label: "Product Group", width: "200px" },
  { id: "hsn", label: "HSN", width: "80px" },
  { id: "gwt", label: "Gr.Wt", width: "90px" },
  { id: "nwt", label: "Net.Wt", width: "90px" },
  { id: "touch", label: "Touch", width: "80px" },
  { id: "fine_wt", label: "Fine Wt", width: "90px" },
  { id: "conv_item", label: "Conv Item", width: "90px" },
  { id: "total_wt", label: "Total Wt", width: "90px" },
  { id: "pcs", label: "Pcs", width: "60px" },
];

function ViewRefinery() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const canEdit = hasPermission(PERMISSIONS.EDIT_REFINERY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await showRefinery({ id: Number(id) });
        if (!isRefineryApiSuccess(res)) {
          toast.error(res?.message || "Failed to load refinery bill");
          navigate("/refinery");
          return;
        }
        const mapped = mapRefineryShowToForm(res);
        if (!mapped) {
          toast.error("Refinery bill not found");
          navigate("/refinery");
          return;
        }
        if (!cancelled) setDoc(mapped);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load refinery bill");
        navigate("/refinery");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="body-wrapper d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!doc) return null;

  const items = doc.items || [];

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0">Refinery — {doc.doc_number}</h4>
            <div className="d-flex gap-2">
              {canEdit && (
                <Button
                  variant="primary"
                  size="sm"
                  className={buttonClass}
                  onClick={() => navigate(`/refinery/${doc.id}/edit`)}
                >
                  <Icon icon="solar:pen-bold" className="me-1" />
                  Edit
                </Button>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                className={buttonClass}
                onClick={() => navigate("/refinery")}
              >
                Back
              </Button>
            </div>
          </div>

          <div className="card-body">
            <div className="row mb-1">
              <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "50px", fontSize: "0.85rem" }}
                  >
                    Date:
                  </label>
                  <span style={{ fontSize: "0.85rem" }}>
                    {formatDateToDDMMYYYY(doc.doc_date)}
                  </span>
                </div>
              </div>
              <div className="col-lg-3 col-md-5 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "65px", fontSize: "0.85rem" }}
                  >
                    Supplier:
                  </label>
                  <span style={{ fontSize: "0.85rem" }}>
                    {doc.supplier_name || "-"}
                  </span>
                </div>
              </div>
              <div className="col-lg-3 col-md-7 col-sm-12 mb-2">
                <span style={{ fontSize: "0.85rem" }}>
                  {doc.supplier_mobile || ""}
                </span>
              </div>
            </div>

            <div
              className={`table-responsive border ${
                buttonTableStyle === "square" ? "rounded-0" : "rounded-2"
              } mt-3 ${cardClass}`}
            >
              <table
                className={`table table-bordered table-hover table-sm mb-0 ${tableClass} theme-header-table`}
              >
                <thead>
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: col.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={item.id ?? idx}>
                      {COLUMNS.map((col) => {
                        let val = "";
                        if (col.id === "no") val = item.line_no ?? idx + 1;
                        else if (col.id === "productGroup")
                          val =
                            item.item_category_name ||
                            item.product_group_name ||
                            "-";
                        else if (col.id === "pcs") val = "1";
                        else val = item[col.id] ?? item[col.id.replace("_", "")] ?? "-";

                        return (
                          <td
                            key={col.id}
                            className="px-2 py-1 text-center"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {val}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    {COLUMNS.map((col) => {
                      if (col.id === "no") {
                        return (
                          <td
                            key={col.id}
                            className="text-center fw-semibold"
                            style={{ fontSize: "0.85rem" }}
                          >
                            Total
                          </td>
                        );
                      }
                      if (col.id === "fine_wt") {
                        return (
                          <td
                            key={col.id}
                            className="text-end fw-semibold px-2"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {doc.total_fine_wt ?? "-"}
                          </td>
                        );
                      }
                      if (col.id === "pcs") {
                        return (
                          <td
                            key={col.id}
                            className="text-center fw-semibold"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {doc.total_pcs ?? items.length}
                          </td>
                        );
                      }
                      return <td key={col.id} />;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="row mb-3 mt-3">
              <div className="col-lg-6 col-md-6 col-sm-6">
                <div className="d-flex align-items-start mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "120px", paddingTop: "6px" }}
                  >
                    Remarks:
                  </label>
                  <textarea
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={doc.remarks || ""}
                    readOnly
                    rows="3"
                    style={{ resize: "none", backgroundColor: "#fff" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewRefinery;
